import { ChangeEvent, FocusEvent, useCallback, useState } from 'react';
import {
  ariaDescribedByIds,
  FormContextType,
  RJSFSchema,
  StrictRJSFSchema,
  WidgetProps,
} from '@rjsf/utils';
import clsx from 'clsx';
import { EyeIcon } from '@heroicons/react/24/outline';
import { EyeSlashIcon } from '@heroicons/react/24/outline';

export function SecretWidget<
  T = any,
  S extends StrictRJSFSchema = RJSFSchema,
  F extends FormContextType = any
>({
  hideError,
  id,
  options = {},
  placeholder,
  value,
  required,
  disabled,
  rawErrors,
  readonly,
  autofocus = false,
  onChange,
  onBlur,
  onFocus,
}: WidgetProps<T, S, F>) {
  const [isVisible, setIsVisible] = useState(false);
  const handleChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) =>
      onChange(value === '' ? options.emptyValue : value),
    [onChange, options.emptyValue]
  );

  const handleBlur = useCallback(
    ({ target: { value } }: FocusEvent<HTMLInputElement>) => onBlur(id, value),
    [onBlur, id]
  );

  const handleFocus = useCallback(
    ({ target: { value } }: FocusEvent<HTMLInputElement>) => onFocus(id, value),
    [id, onFocus]
  );

  const hasError = rawErrors && rawErrors.length > 0 && !hideError;

  const className = clsx(
    'block w-full rounded-md shadow-sm focus:outline-none sm:text-sm dark:bg-slate-900 dark:text-slate-300 border-gray-300 focus:border-indigo-500 focus:ring-indigo-500',
    !hasError &&
      !disabled &&
      !readonly &&
      'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500',
    hasError &&
      'border-red-300 pr-10 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500'
  );

  return (
    <div className="mt-2 relative">
      <input
        id={id}
        name={id}
        className={className}
        value={value ? value : ''}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        readOnly={readonly}
        autoFocus={autofocus}
        type={isVisible ? 'text' : 'password'}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onChange={handleChange}
        aria-describedby={ariaDescribedByIds<T>(id)}
      />
      <button
        type="button"
        className="absolute right-2 top-1/2 -translate-y-1/2"
        onClick={() => setIsVisible(!isVisible)}
      >
        {isVisible ? (
          <EyeIcon className="h-5 w-5 text-gray-400" />
        ) : (
          <EyeSlashIcon className="h-5 w-5 text-gray-400" />
        )}
      </button>
    </div>
  );
}
